import React, { Component } from 'react';
import { throttle } from 'throttle-debounce';
import PropTypes from 'prop-types';
import { AnimatedDiv, AnimatedMenuDiv } from './AnimatedDiv.styled';

const getElementTop = (elm) => {
  let yPos = 0;
  let element = elm;
  while (element && element.offsetTop !== undefined && element.clientTop !== undefined) {
    yPos += element.offsetTop + element.clientTop;
    element = element.offsetParent;
  }
  return yPos;
};

const visibilityHasChanged = (previousVis, currentVis) => {
  const viewport = previousVis.inViewport !== currentVis.inViewport;
  const screen = previousVis.onScreen !== currentVis.onScreen;
  return viewport || screen;
};

class ScrollAnimation extends Component {
  constructor(props) {
    super(props);
    this.serverSide = typeof window === 'undefined';
    this.listener = throttle(50, this.handleScroll.bind(this));
    this.visibility = {
      onScreen: false,
      inViewport: false,
    };

    this.state = {
      modifiers: '',
    };
  }

  componentDidMount() {
    if (!this.serverSide) {
      const { scrollableParentSelector, animatePreScroll } = this.props;
      this.scrollableParent = document.querySelector(scrollableParentSelector || null) || window;
      if (this.scrollableParent && this.scrollableParent.addEventListener) {
        this.scrollableParent.addEventListener('scroll', this.listener);
      } else {
        console.warn(`Cannot find element by locator: ${scrollableParentSelector}`);
      }
      if (animatePreScroll) {
        this.handleScroll();
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.delayedAnimationTimeout);
    if (this.scrollableParent && this.scrollableParent.removeEventListener) {
      this.scrollableParent.removeEventListener('scroll', this.listener);
    }
  }

  onScreen(elementTop, elementBottom) {
    return !this.isAboveScreen(elementBottom) && !this.isBelowScreen(elementTop);
  }

  getScrollPos() {
    if (this.scrollableParent.pageYOffset !== undefined) {
      return this.scrollableParent.pageYOffset;
    }
    return this.scrollableParent.scrollTop;
  }

  getVisibility() {
    const elementTop = getElementTop(this.node) - getElementTop(this.scrollableParent);
    const elementBottom = elementTop + this.node.clientHeight;
    return {
      inViewport: this.inViewport(elementTop, elementBottom),
      onScreen: this.onScreen(elementTop, elementBottom),
    };
  }

  getScrollableParentHeight() {
    if (this.scrollableParent.innerHeight !== undefined) {
      return this.scrollableParent.innerHeight;
    }
    return this.scrollableParent.clientHeight;
  }

  getViewportTop() {
    const { offset } = this.props;
    return this.getScrollPos() + offset;
  }

  getViewportBottom() {
    const { offset } = this.props;
    return this.getScrollPos() + this.getScrollableParentHeight() - offset;
  }

  isAboveViewport(y) {
    return y < this.getViewportTop();
  }

  isInViewport(y) {
    return y >= this.getViewportTop() && y <= this.getViewportBottom();
  }

  isBelowViewport(y) {
    return y > this.getViewportBottom();
  }

  inViewport(elementTop, elementBottom) {
    return (
      this.isInViewport(elementTop)
      || this.isInViewport(elementBottom)
      || (this.isAboveViewport(elementTop) && this.isBelowViewport(elementBottom))
    );
  }

  isAboveScreen(y) {
    return y < this.getScrollPos();
  }

  isBelowScreen(y) {
    return y > this.getScrollPos() + this.getScrollableParentHeight();
  }

  animate(animation) {
    const { delay } = this.props;
    this.delayedAnimationTimeout = setTimeout(() => {
      this.setState({
        modifiers: animation,
      });
    }, delay);
  }

  animateIn() {
    const { animateIn } = this.props;
    this.animate(animateIn);
  }

  animateMenu() {
    const { offset } = this.props;
    const scrollPosition = this.getScrollPos();
    let modifiers = [];

    if (scrollPosition > offset) {
      modifiers = ['headerSmall'];
    }

    if (scrollPosition >= offset + 150) {
      modifiers = [...modifiers, 'headerSmallFixed'];
    }

    if (scrollPosition > offset + 250) {
      modifiers = [...modifiers, 'headerSmallShow'];
    }

    this.setState({
      modifiers,
    });
  }

  handleScroll() {
    const { animateIn, animateMenu } = this.props;

    const currentVis = this.getVisibility();

    if (animateMenu) {
      this.animateMenu();
    }

    if (visibilityHasChanged(this.visibility, currentVis)) {
      clearTimeout(this.delayedAnimationTimeout);
      if (currentVis.inViewport && animateIn) {
        this.animateIn();
      }
      this.visibility = currentVis;
    }
  }

  render() {
    const {
      children, initiallyVisible, duration, inheritStyle, styles, transform,
    } = this.props;
    const { modifiers } = this.state;
    const initialOpacity = initiallyVisible ? 1 : 0;
    return !inheritStyle ? (
      <AnimatedDiv
        ref={(node) => {
          this.node = node;
        }}
        modifiers={modifiers}
        duration={duration}
        initialOpacity={initialOpacity}
        transform={transform}
      >
        {children}
      </AnimatedDiv>
    ) : (
      <AnimatedMenuDiv
        styles={styles}
        ref={(node) => {
          this.node = node;
        }}
        modifiers={modifiers}
        duration={duration}
        initialOpacity={initialOpacity}
      >
        {children}
      </AnimatedMenuDiv>
    );
  }
}

ScrollAnimation.defaultProps = {
  offset: 100,
  duration: 500,
  initiallyVisible: false,
  delay: 0,
  animatePreScroll: true,
  animateIn: '',
  inheritStyle: false,
  scrollableParentSelector: '',
  transform: 'translate(0, 150px)',
};

ScrollAnimation.propTypes = {
  animateIn: PropTypes.string,
  offset: PropTypes.number,
  duration: PropTypes.number,
  delay: PropTypes.number,
  initiallyVisible: PropTypes.bool,
  transform: PropTypes.string,
  scrollableParentSelector: PropTypes.string,
  animatePreScroll: PropTypes.bool,
  children: PropTypes.element.isRequired,
  inheritStyle: PropTypes.bool,
};

export default ScrollAnimation;
